import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { register, authAxios } from '../lib/auth';
import { formatPhone, isValidPhone, toApiPhone } from '../lib/phone';
import BrutalistNavbar from '../components/layout/BrutalistNavbar';
import BrutalistFooter from '../components/layout/BrutalistFooter';

const inputClass = "w-full bg-gray-50 border-2 border-black rounded-none px-3 py-2 font-mono text-sm placeholder:text-gray-500 focus:outline-none focus:bg-white";
const labelClass = "block text-[10px] font-bold uppercase tracking-wider mb-1 font-mono text-black/60";
const errorClass = "text-xs text-red-600 font-mono mt-1 uppercase";

export default function RegisterPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    last_name: '', first_name: '', middle_name: '',
    email: '', phone: '',
    password: '', password2: '',
    passport_series_number: '', passport_address: '',
    fact_address: '',
    passport_issued_by: '', passport_issued_date: '',
  });
  const [factSame, setFactSame] = useState(true);  // по умолчанию = совпадает
  const [photoMain, setPhotoMain] = useState(null);   // 1-я страница (с фото и ФИО)
  const [photoReg, setPhotoReg] = useState(null);     // прописка
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // SMS-верификация телефона
  const [smsState, setSmsState] = useState('idle');   // idle | sent | verified
  const [smsCode, setSmsCode] = useState('');
  const [verifyToken, setVerifyToken] = useState('');
  const [smsBusy, setSmsBusy] = useState(false);
  const [smsError, setSmsError] = useState('');
  const [smsCooldown, setSmsCooldown] = useState(0);
  const [skipVerify, setSkipVerify] = useState(false);  // звонок не дошёл — регистрация без кода, проверит оператор

  // обратный отсчёт ресенда
  useEffect(() => {
    if (smsCooldown <= 0) return;
    const t = setTimeout(() => setSmsCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [smsCooldown]);

  const sendSmsCode = async () => {
    setSmsError('');
    if (!isValidPhone(form.phone)) { setSmsError('Введите телефон полностью'); return; }
    setSmsBusy(true);
    try {
      // Сначала пробуем обратный звонок (дешевле SMS)
      await authAxios.post('/api/auth/call/send', { phone: toApiPhone(form.phone), purpose: 'register' });
      setSmsState('sent');
      setSmsCooldown(60);
      setSmsError('');
    } catch (err) {
      // Если call недоступен — пробуем SMS как fallback
      try {
        const { data } = await authAxios.post('/api/auth/sms/send', { phone: toApiPhone(form.phone), purpose: 'register' });
        setSmsState('sent');
        setSmsCooldown(60);
        // ВАЖНО: внутреннее значение data.mode === 'test' означает что бэк в dry-run (нет SMS_API_KEY),
        // НЕ показываем это клиенту как «TEST-режим» — это путает. Просим связаться по телефону.
        if (data.mode === 'test') {
          setSmsError('Сервис временно недоступен. Свяжитесь с нами по телефону +7 918 436-22-55 — оператор оформит регистрацию.');
        }
      } catch (err2) {
        const detail = err2?.response?.data?.detail || err?.response?.data?.detail || '';
        // Rate-limit сообщения от бэка показываем как есть, остальное — типовая ошибка
        if (typeof detail === 'string' && (detail.includes('Слишком много') || detail.includes('Подождите'))) {
          setSmsError(detail);
        } else {
          setSmsError('Не удалось отправить код. Попробуйте через несколько минут или позвоните +7 918 436-22-55.');
        }
      }
    } finally {
      setSmsBusy(false);
    }
  };

  const verifySmsCode = async () => {
    setSmsError('');
    setSmsBusy(true);
    try {
      const { data } = await authAxios.post('/api/auth/sms/verify', {
        phone: toApiPhone(form.phone), code: smsCode.trim(), purpose: 'register',
      });
      setVerifyToken(data.verify_token);
      setSmsState('verified');
    } catch (err) {
      setSmsError(err?.response?.data?.detail || 'Неверный код');
    } finally {
      setSmsBusy(false);
    }
  };

  const resetSms = () => { setSmsState('idle'); setSmsCode(''); setVerifyToken(''); setSmsError(''); };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!consent) { setError('Подтвердите согласие на обработку персональных данных'); return; }
    if (!isValidPhone(form.phone)) { setError('Введите телефон полностью (11 цифр)'); return; }
    // Верификация телефона: либо код подтверждён, либо пользователь выбрал «звонок не приходит»
    if (!skipVerify && (smsState !== 'verified' || !verifyToken)) {
      setError('Подтвердите номер телефона кодом из звонка, либо нажмите «Звонок не приходит?»');
      return;
    }
    if (form.password.length < 6) { setError('Пароль не короче 6 символов'); return; }
    if (form.password !== form.password2) { setError('Пароли не совпадают'); return; }
    if (!photoMain) { setError('Прикрепите фото 1-й страницы паспорта (с фото и ФИО)'); return; }
    if (!photoReg) { setError('Прикрепите фото страницы с пропиской'); return; }

    setBusy(true);
    try {
      const fd = new FormData();
      for (const [k, v] of Object.entries(form)) {
        if (k === 'password2') continue;
        if (k === 'phone') { fd.append('phone', toApiPhone(v)); continue; }
        // если фактический адрес совпадает — backend сам подставит passport_address
        if (k === 'fact_address' && factSame) { fd.append('fact_address', ''); continue; }
        fd.append(k, v || '');
      }
      fd.append('consent', 'true');
      fd.append('fact_same_as_passport', factSame ? 'true' : 'false');
      // если пропустили верификацию — токен пустой, бэк создаст юзера с phone_verified=0
      fd.append('verify_token', skipVerify ? '' : verifyToken);
      // Бэк принимает один файл — склеиваем 2 фото в один PDF/архив на стороне фронта нельзя без либ,
      // поэтому передаём оба файла; backend `passport_photo` принимает основной,
      // а `passport_photo_2` — прописку (если не примет — будет проигнорирован, не сломает совместимость).
      fd.append('passport_photo', photoMain);
      fd.append('passport_photo_2', photoReg);
      await register(fd);
      navigate('/cabinet');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Ошибка регистрации';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F3EE] flex flex-col">
      <BrutalistNavbar />
      <main className="flex-1 w-full max-w-6xl mx-auto px-4 pt-28 pb-6">
        <div className="bg-white border-2 border-black p-5 lg:p-6 shadow-[6px_6px_0_0_#000]">
          <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold uppercase leading-none">Запрос данных</h1>
              <p className="font-mono text-xs text-black/60 mt-1">для получения доступа в центр хранения вещей</p>
            </div>
            <p className="font-mono text-[11px] text-black/50">
              Уже зарегистрированы? <Link to="/login" className="underline hover:text-accent">Войти</Link>
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-3">

            {/* Row 1: ФИО */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className={labelClass}>Фамилия *</label>
                <input className={inputClass} value={form.last_name} onChange={upd('last_name')} required />
              </div>
              <div>
                <label className={labelClass}>Имя *</label>
                <input className={inputClass} value={form.first_name} onChange={upd('first_name')} required />
              </div>
              <div>
                <label className={labelClass}>Отчество</label>
                <input className={inputClass} value={form.middle_name} onChange={upd('middle_name')} />
              </div>
            </div>

            {/* Row 2: контакты + пароли — 4 колонки на десктопе */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className={labelClass}>E-mail *</label>
                <input type="email" className={inputClass} value={form.email} onChange={upd('email')} required />
              </div>
              <div>
                <label className={labelClass}>
                  Телефон * {smsState === 'verified' && <span className="text-green-700 normal-case font-normal">✓ подтверждён</span>}
                </label>
                <div className="flex gap-1">
                  <input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    maxLength={18}
                    className={`${inputClass} flex-1 ${smsState==='verified'?'bg-green-50':''}`}
                    value={form.phone}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, phone: formatPhone(e.target.value) }));
                      if (smsState !== 'idle') resetSms();
                    }}
                    placeholder="+7 (___) ___-__-__"
                    disabled={smsState === 'verified'}
                    required
                  />
                  {smsState !== 'verified' && (
                    <button
                      type="button"
                      onClick={sendSmsCode}
                      disabled={smsBusy || !isValidPhone(form.phone) || smsCooldown > 0}
                      className="shrink-0 px-3 bg-black text-white border-2 border-black text-[10px] uppercase font-mono disabled:bg-gray-300 disabled:border-gray-300"
                      title="Заказать звонок с кодом"
                    >
                      {smsBusy ? '…' : smsCooldown > 0 ? `${smsCooldown}с` : '📞 Звонок'}
                    </button>
                  )}
                </div>
                {smsState === 'sent' && (
                  <>
                    <p className="text-[10px] font-mono text-black/60 mt-1 leading-tight">
                      📞 Сейчас на ваш номер поступит звонок. Введите <strong>последние 4 цифры</strong> номера звонящего — это и есть код. Отвечать не нужно.
                    </p>
                    <div className="mt-1 flex gap-1">
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="Код (4-6 цифр)"
                        value={smsCode}
                        onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className={`${inputClass} flex-1`}
                        autoFocus
                      />
                      <button type="button" onClick={verifySmsCode}
                        disabled={smsBusy || (smsCode.length !== 4 && smsCode.length !== 6)}
                        className="shrink-0 px-3 bg-[#E63B2E] text-white border-2 border-black text-[10px] uppercase font-mono disabled:bg-gray-300">
                        {smsBusy ? '…' : 'OK'}
                      </button>
                    </div>
                  </>
                )}
                {smsError && <p className="text-[10px] text-red-600 font-mono mt-1">// {smsError}</p>}

                {/* Фолбэк: звонок не приходит — регистрация без кода, телефон проверит оператор */}
                {smsState !== 'verified' && !skipVerify && (
                  <button
                    type="button"
                    onClick={() => { setSkipVerify(true); setSmsError(''); }}
                    className="mt-2 text-[10px] font-mono text-black/50 underline hover:text-[#E63B2E]"
                  >
                    Звонок не приходит? Зарегистрироваться без подтверждения →
                  </button>
                )}
                {skipVerify && (
                  <div className="mt-2 p-2 border-2 border-yellow-600 bg-yellow-50 font-mono text-[10px] leading-tight">
                    ⚠ Телефон будет проверен оператором при модерации. Регистрация продолжится без кода.
                    <button type="button" onClick={() => setSkipVerify(false)}
                      className="ml-1 underline text-black/60 hover:text-[#E63B2E]">отмена</button>
                  </div>
                )}
                {smsState === 'verified' && (
                  <p className="text-[10px] font-mono text-green-700 mt-1">✓ Телефон подтверждён</p>
                )}
              </div>
              <div>
                <label className={labelClass}>Пароль * (для входа в кабинет)</label>
                <input
                  type="password"
                  className={inputClass}
                  value={form.password}
                  onChange={upd('password')}
                  placeholder="мин. 6 символов"
                  autoComplete="new-password"
                  required
                  minLength={6}
                />
                <p className="text-[9px] font-mono text-black/50 mt-0.5">// потом войдёте по телефону + пароль</p>
              </div>
              <div>
                <label className={labelClass}>Повторите пароль *</label>
                <input
                  type="password"
                  className={inputClass}
                  value={form.password2}
                  onChange={upd('password2')}
                  placeholder="ещё раз"
                  autoComplete="new-password"
                  required
                  minLength={6}
                />
              </div>
            </div>

            {/* Row 3: паспорт — серия+номер | дата | кем выдан */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className={labelClass}>Серия и номер паспорта *</label>
                <input className={inputClass} value={form.passport_series_number} onChange={upd('passport_series_number')} placeholder="0000 000000" required />
              </div>
              <div>
                <label className={labelClass}>Когда выдан *</label>
                <input type="date" className={inputClass} value={form.passport_issued_date} onChange={upd('passport_issued_date')} required />
              </div>
              <div>
                <label className={labelClass}>Кем выдан *</label>
                <input className={inputClass} value={form.passport_issued_by} onChange={upd('passport_issued_by')} required />
              </div>
            </div>

            {/* Row 4: адрес регистрации — full-width */}
            <div>
              <label className={labelClass}>Адрес регистрации (по паспорту) *</label>
              <input className={inputClass} value={form.passport_address} onChange={upd('passport_address')} required />
            </div>

            {/* Row 4b: фактический адрес + чекбокс */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={labelClass + " mb-0"}>Фактический адрес проживания {factSame ? '' : '*'}</label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={factSame} onChange={(e) => setFactSame(e.target.checked)}
                    className="w-3.5 h-3.5 border border-black accent-black cursor-pointer" />
                  <span className="font-mono text-[10px] uppercase tracking-wider text-black/60">Совпадает с адресом регистрации</span>
                </label>
              </div>
              <input
                className={`${inputClass} ${factSame ? 'opacity-50 cursor-not-allowed' : ''}`}
                value={factSame ? form.passport_address : form.fact_address}
                onChange={upd('fact_address')}
                placeholder={factSame ? 'Использовать адрес регистрации' : 'г. Краснодар, ул. ...'}
                disabled={factSame}
                required={!factSame}
              />
            </div>

            {/* Row 5: фото паспорта — ДВА отдельных поля */}
            <div className="border-2 border-black bg-yellow-50/40 p-4">
              <div className="font-mono text-xs uppercase tracking-wider mb-3 text-black/80 font-bold">
                Фото паспорта — нужны <span className="text-[#E63B2E]">обе страницы</span> *
              </div>
              <p className="font-mono text-[10px] text-black/60 mb-4 leading-relaxed">
                Прикрепите две фотографии или скана:<br />
                • <strong>1-я страница</strong> — разворот с фото, ФИО, серией и номером<br />
                • <strong>прописка</strong> — страница со штампом регистрации (адресом по паспорту)
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-wider mb-1 text-black/70">
                    📷 1) Страница с фото
                  </div>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
                    onChange={(e) => setPhotoMain(e.target.files?.[0] || null)}
                    className={`w-full bg-white border-2 rounded-none px-3 py-2 font-mono text-xs cursor-pointer file:mr-3 file:px-3 file:py-1 file:border file:border-black file:bg-white file:font-mono file:text-[10px] file:cursor-pointer ${photoMain ? 'border-green-600' : 'border-black'}`}
                    required
                  />
                  {photoMain
                    ? <p className="text-[10px] font-mono text-green-700 mt-1">✓ {photoMain.name} ({(photoMain.size/1024).toFixed(0)} КБ)</p>
                    : <p className="text-[10px] font-mono text-red-600 mt-1">⚠ файл не выбран</p>
                  }
                </div>

                <div>
                  <div className="font-mono text-[10px] uppercase tracking-wider mb-1 text-black/70">
                    📷 2) Прописка
                  </div>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
                    onChange={(e) => setPhotoReg(e.target.files?.[0] || null)}
                    className={`w-full bg-white border-2 rounded-none px-3 py-2 font-mono text-xs cursor-pointer file:mr-3 file:px-3 file:py-1 file:border file:border-black file:bg-white file:font-mono file:text-[10px] file:cursor-pointer ${photoReg ? 'border-green-600' : 'border-black'}`}
                    required
                  />
                  {photoReg
                    ? <p className="text-[10px] font-mono text-green-700 mt-1">✓ {photoReg.name} ({(photoReg.size/1024).toFixed(0)} КБ)</p>
                    : <p className="text-[10px] font-mono text-red-600 mt-1">⚠ файл не выбран</p>
                  }
                </div>
              </div>
            </div>

            {/* Row 6: согласие + ошибка + кнопка */}
            <div className="flex flex-col lg:flex-row lg:items-center gap-3 pt-2">
              <label className="flex items-start gap-2 cursor-pointer flex-1">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 w-4 h-4 border-2 border-black accent-black cursor-pointer flex-shrink-0" />
                <span className="font-mono text-[11px] text-black/70 leading-tight">
                  Я принимаю <a href="/oferta" target="_blank" rel="noopener noreferrer" className="underline hover:text-accent font-bold">Договор-оферту</a>, <a href="/rules" target="_blank" rel="noopener noreferrer" className="underline hover:text-accent font-bold">Правила</a> и согласен с <Link to="/privacy" className="underline hover:text-accent" target="_blank">Политикой обработки персональных данных</Link> *
                </span>
              </label>

              <button
                type="submit"
                disabled={busy || !consent}
                className="lg:w-auto w-full px-8 h-11 bg-[#E63B2E] hover:bg-[#D62B1E] disabled:bg-gray-300 text-white rounded-none border-2 border-black uppercase font-bold tracking-widest text-xs transition-all hover:translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[3px_3px_0_0_#000] disabled:hover:translate-x-0 disabled:hover:translate-y-0 disabled:hover:shadow-none"
              >
                {busy ? 'Отправка...' : 'Зарегистрироваться'}
              </button>
            </div>

            {error && <p className={errorClass}>// {error}</p>}
          </form>
        </div>
      </main>
      <BrutalistFooter />
    </div>
  );
}
